import type { VideoSet } from "@pax/api-contract/common";
import { cn } from "@pax/ui";
import { type RefObject, useEffect, useRef, useState } from "react";
import type { VideoDecoderPreference } from "../lib/playback";
import { useVideoPlayback } from "../lib/use-video-playback";
import { VideoSurface } from "./VideoSurface";

/**
 * A video that plays silently and repeats while it is mounted and on screen, filling its box. It
 * fades in over whatever is drawn beneath it once its first frame is ready, and draws nothing under
 * reduced motion or, with the `native` decoder, where the browser can't decode it.
 */
export function LoopingVideo({
  video,
  decoder = "native",
  onFrame,
  className,
}: {
  video: VideoSet;
  decoder?: VideoDecoderPreference;
  /** Called with the media time of each frame as it is shown. */
  onFrame?: (mediaTime: number) => void;
  className?: string;
}) {
  if (usePrefersReducedMotion()) return null;
  return <PlayingLoop video={video} decoder={decoder} onFrame={onFrame} className={className} />;
}

function PlayingLoop({
  video,
  decoder,
  onFrame,
  className,
}: {
  video: VideoSet;
  decoder: VideoDecoderPreference;
  onFrame?: (mediaTime: number) => void;
  className?: string;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const player = useVideoPlayback(video, { decoder, loop: true, onFrame });
  const onScreen = useOnScreen(frame);
  const pageVisible = usePageVisible();
  const shouldPlay = onScreen && pageVisible;
  const { play, pause } = player;

  useEffect(() => {
    if (shouldPlay) play();
    else pause();
  }, [shouldPlay, play, pause]);

  return (
    <div
      ref={frame}
      aria-hidden
      className={cn(
        "pointer-events-none relative overflow-hidden transition-opacity duration-500",
        player.state.hasFrame ? "opacity-100" : "opacity-0",
        className,
      )}
    >
      <VideoSurface player={player} className="absolute inset-0 size-full object-cover" />
    </div>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const changed = () => setReduced(query.matches);
    query.addEventListener("change", changed);
    return () => query.removeEventListener("change", changed);
  }, []);
  return reduced;
}

function useOnScreen(element: RefObject<HTMLElement | null>) {
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    const target = element.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting ?? false),
      { threshold: 0.01 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [element]);
  return onScreen;
}

function usePageVisible() {
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const changed = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", changed);
    return () => document.removeEventListener("visibilitychange", changed);
  }, []);
  return visible;
}
